import { zodResolver } from "@hookform/resolvers/zod";
import {
  NOMINEE_RELATIONSHIP,
  IDENTITY_TYPE_CHOICES,
  getIdentityNumberPlaceholder,
} from "creso-core/constants";
import { useCountryOptions, useLocationInfo } from "creso-core/hooks";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  Button,
  Checkbox,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormField,
  FormInput,
  FormSelect,
  V2Label,
  VisuallyHidden,
  ScrollArea,
  FormPrefixedInput,
  NumberCounterInput,
  Badge,
  FormToggleGroup,
} from "web-design-system";
import { z } from "zod";
import { NomineeFormSchema } from "./form-schema";
import { useAccountForm } from "./context";
import { InvestorAddressDetails } from "creso-core/types";
import { ProgressPills } from "./progress-pills";
import { ConditionalRenderer } from "../primitives";
import { ArrowLeft, InfoIcon } from "lucide-react";

export type NomineeFormValues = z.infer<typeof NomineeFormSchema>;

export type PercentageAllocation = {
  nominee_id?: string | number;
  name: string;
  applicable_percentage: number;
};

export function NomineeEditDialog({
  trigger,
  data,
  onSave,
  totalPercentageUsed = 0,
  mode = "update",
  clientAddress,
  allNominees = [],
  defaultOpen = false,
  onOpenChange,
}: {
  trigger: React.ReactNode;
  data?: NomineeFormValues;
  onSave: (
    data: NomineeFormValues,
    percentageAllocations: PercentageAllocation[]
  ) => Promise<boolean> | boolean;
  totalPercentageUsed?: number;
  mode?: "view" | "update" | "add";
  clientAddress?: InvestorAddressDetails;
  allNominees?: NomineeFormValues[];
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (value: boolean) => {
    setOpenState(value);
    onOpenChange?.(value);
  };
  const [isEditMode, setIsEditMode] = useState(mode !== "view");
  const [step, setStep] = useState<1 | 2>(1);
  const [percentageAllocations, setPercentageAllocations] = useState<
    PercentageAllocation[]
  >([]);
  const countryOptions = useCountryOptions();

  const isExistingNominee = mode !== "add";

  const isViewMode = !isEditMode;

  // Calculate max percentage available for this nominee
  const currentNomineePercentage = data?.applicable_percentage || 0;
  const maxPercentage = 100 - totalPercentageUsed + currentNomineePercentage;

  const form = useForm<NomineeFormValues>({
    values: {
      nominate: true,
      nominee_id: data?.nominee_id,
      name: data?.name || "",
      relationship: data?.relationship || "",
      identity_type: data?.identity_type || "",
      identity_number: data?.identity_number || "",
      email: data?.email || "",
      mobile: data?.mobile || "",
      date_of_birth: data?.date_of_birth || null,
      guardian_name: data?.guardian_name || "",
      guardian_pan: data?.guardian_pan || "",
      address_line_1: data?.address_line_1 || "",
      address_line_2: data?.address_line_2 || "",
      address_line_3: data?.address_line_3 || "",
      country: data?.country || "",
      city: data?.city || "",
      pin_code: data?.pin_code || "",
      applicable_percentage:
        data?.applicable_percentage || Math.min(100, maxPercentage),
    },
    resolver: zodResolver(NomineeFormSchema),
  });

  // Reset edit mode and step when dialog opens/closes or edit prop changes
  useEffect(() => {
    if (open) {
      setIsEditMode(mode !== "view");
      setStep(1);
    } else {
      form.reset();
      setStep(1);
    }
  }, [open, form, mode]);

  const { refetch } = useLocationInfo(form.watch("pin_code"), false);

  const handlePostalCodeBlur = async () => {
    const isValid = await form.trigger("pin_code");
    if (isValid) {
      const result = await refetch();
      if (result.data) {
        form.setValue("city", result.data.city);
      }
    }
  };

  const fillAddressDetails = (value: boolean) => {
    setUseClientAddress(value);
    if (!value) {
      form.setValue("address_line_1", "");
      form.setValue("address_line_2", "");
      form.setValue("address_line_3", "");
      form.setValue("country", "");
      form.setValue("pin_code", "");
      form.setValue("city", "");
      return;
    }
    if (!clientAddress) return;
    form.setValue("address_line_1", clientAddress.line1 || "");
    form.setValue("address_line_2", clientAddress.line2 || "");
    form.setValue("address_line_3", clientAddress.line3 || "");
    form.setValue("country", clientAddress.country || "");
    form.setValue("pin_code", clientAddress.postal_code || "");
    form.setValue("city", clientAddress.city || "");
  };

  const [useClientAddress, setUseClientAddress] = useState(false);

  useEffect(() => {
    setUseClientAddress(() => {
      if (clientAddress?.city !== form.getValues("city")) return false;
      if (clientAddress?.country !== form.getValues("country")) return false;
      if (clientAddress?.postal_code !== form.getValues("pin_code"))
        return false;
      if (clientAddress?.line1 !== form.getValues("address_line_1"))
        return false;
      if (clientAddress?.line2 !== form.getValues("address_line_2"))
        return false;
      if (clientAddress?.line3 !== form.getValues("address_line_3"))
        return false;
      return true;
    });
  }, [clientAddress, form]);

  const handleContinue = async (currentValues: NomineeFormValues) => {
    if (isExistingNominee) {
      // For edit: use existing percentages, update the edited nominee's name
      const allocations: PercentageAllocation[] = allNominees.map((n) => ({
        nominee_id: n.nominee_id,
        name:
          n.nominee_id === currentValues.nominee_id
            ? currentValues.name
            : n.name,
        applicable_percentage: n.applicable_percentage,
      }));
      setPercentageAllocations(allocations);
    } else {
      const newAllocation = Math.floor(100 / (allNominees.length + 1));
      const allocations: PercentageAllocation[] = [
        ...allNominees.map((n) => ({
          nominee_id: n.nominee_id,
          name: n.name,
          applicable_percentage: newAllocation,
        })),
        {
          name: currentValues.name,
          applicable_percentage: 100 - allNominees.length * newAllocation,
        },
      ];
      setPercentageAllocations(allocations);
    }

    setStep(2);
  };

  const adjustPercentage = (index: number, value: number) => {
    setPercentageAllocations((prev) => {
      const newAllocations = [...prev];
      const newValue = value;
      if (newValue < 0 || newValue > 100) return prev;
      newAllocations[index] = {
        ...newAllocations[index],
        applicable_percentage: newValue,
      };
      return newAllocations;
    });
  };

  const totalAllocatedPercentage = percentageAllocations.reduce(
    (sum, a) => sum + a.applicable_percentage,
    0
  );

  const { onReviewChanges } = useAccountForm();

  const handlePercentageSubmit = async (values: NomineeFormValues) => {
    if (totalAllocatedPercentage !== 100) return;
    const result = await onSave(values, percentageAllocations);
    if (result) {
      setOpen(false);
      onReviewChanges?.();
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="h-[85vh] p-0 w-120 max-w-[95vw]"
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>
              {isViewMode ? "View" : isExistingNominee ? "Edit" : "Add"} nominee
            </DialogTitle>
            <DialogDescription>
              Dialog to{" "}
              {isViewMode ? "view" : isExistingNominee ? "update" : "add"}{" "}
              nominee details
            </DialogDescription>
          </DialogHeader>
        </VisuallyHidden>
        <Form {...form}>
          <form
            className="flex flex-col py-5 pt-3 flex-1 overflow-hidden"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (step === 1 && !isViewMode) {
                form.handleSubmit(handleContinue)(event);
              } else if (step === 2) {
                form.handleSubmit(handlePercentageSubmit)(event);
              }
            }}
          >
            {/* Step 1: Nominee Details */}
            {step === 1 && (
              <>
                <div className="flex pb-4 px-5 items-center justify-start w-full">
                  <div className="h-9 w-0" />
                  <span className="mb-0 text-lg mr-auto font-semibold">
                    {mode === "add" && "Add Nominee"}
                    {mode === "update" && "Edit Nominee"}
                    {mode === "view" &&
                      (isEditMode ? "Edit nominee" : "Nominee details")}
                  </span>
                  {!isViewMode && <ProgressPills total={2} current={1} />}
                </div>
                <ScrollArea className="flex-1 px-5">
                  <div className="flex flex-col gap-4 px-1">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormInput
                          label="Name*"
                          placeholder="Enter nominee name"
                          disabled={isViewMode}
                          {...field}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="relationship"
                      render={({ field }) => (
                        <FormSelect
                          label="Relationship*"
                          options={NOMINEE_RELATIONSHIP}
                          placeholder="Select relationship"
                          disabled={isViewMode}
                          {...field}
                        />
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="identity_type"
                      render={({ field }) => (
                        <FormToggleGroup
                          {...field}
                          options={IDENTITY_TYPE_CHOICES}
                          disabled={isViewMode}
                          label="Identity details"
                          onChange={(value) => {
                            field.onChange(value);
                            if (!value) {
                              form.setValue("identity_number", "", {
                                shouldValidate: true,
                              });
                            }
                          }}
                        />
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="identity_number"
                      render={({ field }) => (
                        <FormInput
                          label="Identity number"
                          placeholder={getIdentityNumberPlaceholder(
                            form.watch("identity_type")
                          )}
                          disabled={isViewMode}
                          {...field}
                          onChange={(val) => {
                            field.onChange(val?.toLocaleUpperCase());
                          }}
                        />
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="mobile"
                      render={({ field }) => (
                        <FormPrefixedInput
                          type="tel"
                          label="Phone Number"
                          prefix=""
                          placeholder="Enter phone number"
                          disabled={isViewMode}
                          {...field}
                        >
                          +91
                        </FormPrefixedInput>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormInput
                          label="Email"
                          placeholder="Enter email address"
                          disabled={isViewMode}
                          {...field}
                        />
                      )}
                    />

                    {clientAddress && !isViewMode && (
                      <div className="flex flex-row text-xs items-center gap-1">
                        <Checkbox
                          id="same-as-client"
                          disabled={isViewMode}
                          checked={useClientAddress}
                          onCheckedChange={fillAddressDetails}
                        />
                        <V2Label
                          className="font-normal text-primary"
                          htmlFor="same-as-client"
                        >
                          Use client&apos;s address for nominee
                        </V2Label>
                      </div>
                    )}

                    <div className="flex rounded-lg gap-4 px-4 py-3 border border-border flex-col">
                      <span className="font-medium text-sm">Address</span>
                      <FormField
                        control={form.control}
                        name="address_line_1"
                        render={({ field }) => (
                          <FormInput
                            label="Line 1"
                            placeholder="Enter address line 1"
                            disabled={isViewMode}
                            {...field}
                          />
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="address_line_2"
                        render={({ field }) => (
                          <FormInput
                            label="Line 2"
                            placeholder="Enter address line 2"
                            disabled={isViewMode}
                            {...field}
                            value={field.value ?? ""}
                          />
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="address_line_3"
                        render={({ field }) => (
                          <FormInput
                            label="Line 3"
                            placeholder="Enter address line 3"
                            disabled={isViewMode}
                            {...field}
                            value={field.value ?? ""}
                          />
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="pin_code"
                        render={({ field }) => (
                          <FormInput
                            label="Postal code"
                            placeholder="Enter postal code"
                            disabled={isViewMode}
                            {...field}
                            onBlur={handlePostalCodeBlur}
                          />
                        )}
                      />

                      <div className="grid gap-4 grid-cols-2">
                        <FormField
                          control={form.control}
                          name="city"
                          render={({ field }) => (
                            <FormInput
                              label="City or Town"
                              placeholder="Enter city"
                              disabled={isViewMode}
                              {...field}
                            />
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="country"
                          render={({ field }) => (
                            <FormSelect
                              label="Country"
                              options={countryOptions}
                              disabled={isViewMode}
                              {...field}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </ScrollArea>

                <DialogFooter className="mt-4 px-5">
                  <ConditionalRenderer condition={isViewMode}>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setOpen(false)}
                    >
                      Close
                    </Button>
                    <Button type="button" onClick={() => setIsEditMode(true)}>
                      Edit nominee
                    </Button>
                  </ConditionalRenderer>
                  <ConditionalRenderer condition={!isViewMode}>
                    <Button
                      variant="outline"
                      onClick={() => setOpen(false)}
                      type="button"
                    >
                      Cancel
                    </Button>
                    <Button type="submit">Continue</Button>
                  </ConditionalRenderer>
                </DialogFooter>
              </>
            )}

            {/* Step 2: Percentage Share */}
            {step === 2 && (
              <>
                <div className="flex pb-4 pl-2 pr-5 items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      onClick={() => setStep(1)}
                    >
                      <ArrowLeft className="size-4" />
                    </Button>
                    <span className="mb-0 text-lg font-semibold">
                      {isExistingNominee ? "Edit nominee" : "Add nominee"}
                    </span>
                  </div>
                  <ProgressPills total={2} current={2} />
                </div>

                <div className="flex flex-col px-5 flex-1">
                  <span className="text-sm font-semibold mb-4">
                    Percentage share
                  </span>
                  <div className="flex flex-col gap-4">
                    {percentageAllocations.map((allocation, index) => (
                      <div
                        key={
                          allocation.nominee_id?.toString() || allocation.name
                        }
                        className="flex items-center gap-2 justify-between"
                      >
                        <div className="flex gap-1 items-center">
                          <span className="text-sm line-clamp-2">
                            {allocation.name}
                          </span>
                          {!allocation.nominee_id && (
                            <Badge
                              variant="custom"
                              className="text-tag-green-foreground bg-tag-green"
                            >
                              New
                            </Badge>
                          )}
                        </div>
                        <div className="w-48 shrink-0">
                          <NumberCounterInput
                            value={allocation.applicable_percentage}
                            onChange={(val) => adjustPercentage(index, val)}
                            step={1}
                            displayFormatter={(val) => `${val}%`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between mx-5 px-3 py-2 bg-secondary rounded-lg mt-auto">
                  <div className="text-sm flex items-center gap-2">
                    {totalAllocatedPercentage === 100 ? (
                      "Total allocation"
                    ) : (
                      <>
                        <InfoIcon className="size-4" />{" "}
                        {totalAllocatedPercentage > 100
                          ? `${totalAllocatedPercentage - 100}% over the limit`
                          : `${100 - totalAllocatedPercentage}% remaining`}
                      </>
                    )}
                  </div>
                  <span className={cn("text-sm font-bold")}>
                    {totalAllocatedPercentage}%
                  </span>
                </div>

                <DialogFooter className="mt-4 px-5">
                  <Button
                    variant="outline"
                    onClick={() => setOpen(false)}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      totalAllocatedPercentage !== 100 ||
                      percentageAllocations.some(
                        (p) => Number(p.applicable_percentage) === 0
                      )
                    }
                  >
                    {isExistingNominee ? "Update Nominee" : "Add Nominee"}
                  </Button>
                </DialogFooter>
              </>
            )}
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
